import React, { useState } from "react";
import Navbar from "../components/sub-comp/Navbar.jsx";
import Footer from "../components/sub-comp/MainFooter.jsx";
import FloatingSocialPanels from "../components/sub-comp/FloatingSocialPanels";
import {
  FaGraduationCap,
  FaBook,
  FaUsers,
  FaCalendarAlt,
  FaTrophy,
  FaLightbulb,
  FaSearch,
  FaArrowRight,
  FaMicroscope,
  FaLaptop,
  FaChartLine,
  FaGlobe,
  FaCode,
  FaShieldAlt,
  FaDatabase,
  FaNetworkWired,
  FaMobileAlt,
  FaCloud,
  FaRobot,
} from "react-icons/fa";

const Academics = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const academicPhilosophy = {
    title: "Excellence in Education",
    description:
      "At JR Institute, we believe in providing a transformative educational experience that combines rigorous academic training with practical, real-world applications. Our curriculum is designed to prepare students for the challenges of tomorrow's technology landscape.",
    principles: [
      "Innovation in Teaching Methods",
      "Industry-Relevant Curriculum",
      "Hands-on Learning Experience",
      "Research and Development Focus",
      "Global Perspective and Local Impact",
    ],
  };

  const featuredPrograms = [
    {
      id: 1,
      name: "Information Technology",
      description:
        "Comprehensive IT program covering software development, networking, and system administration.",
      icon: FaLaptop,
      duration: "3 Years",
      credits: "120",
      careerOutcomes: [
        "Software Developer",
        "System Administrator",
        "IT Consultant",
      ],
      highlights: [
        "Industry Certifications",
        "Internship Program",
        "Capstone Project",
      ],
    },
    {
      id: 2,
      name: "Cybersecurity",
      description:
        "Advanced cybersecurity program focusing on threat analysis, ethical hacking, and digital forensics.",
      icon: FaShieldAlt,
      duration: "3 Years",
      credits: "120",
      careerOutcomes: [
        "Cybersecurity Analyst",
        "Ethical Hacker",
        "Security Consultant",
      ],
      highlights: [
        "Hands-on Labs",
        "Industry Partnerships",
        "Certification Prep",
      ],
    },
    {
      id: 3,
      name: "Computer Science",
      description:
        "Theoretical and practical computer science education with focus on algorithms and software engineering.",
      icon: FaCode,
      duration: "4 Years",
      credits: "160",
      careerOutcomes: [
        "Software Engineer",
        "Data Scientist",
        "Research Scientist",
      ],
      highlights: [
        "Research Opportunities",
        "Advanced Mathematics",
        "AI/ML Focus",
      ],
    },
    {
      id: 4,
      name: "Data Science",
      description:
        "Interdisciplinary program combining statistics, programming, and domain expertise for data analysis.",
      icon: FaChartLine,
      duration: "3 Years",
      credits: "120",
      careerOutcomes: [
        "Data Analyst",
        "Data Scientist",
        "Business Intelligence",
      ],
      highlights: ["Big Data Tools", "Machine Learning", "Industry Projects"],
    },
  ];

  const academicResources = [
    {
      id: 1,
      title: "Learning Management System",
      description:
        "Access course materials, assignments, and grades through our comprehensive LMS platform.",
      icon: FaBook,
      features: [
        "Online Course Materials",
        "Assignment Submission",
        "Grade Tracking",
        "Discussion Forums",
      ],
    },
    {
      id: 2,
      title: "Research Facilities",
      description:
        "State-of-the-art research labs equipped with latest technology and software tools.",
      icon: FaMicroscope,
      features: [
        "Advanced Computing",
        "Research Software",
        "Collaboration Tools",
        "Data Storage",
      ],
    },
    {
      id: 3,
      title: "Academic Support",
      description:
        "Comprehensive support services including tutoring, writing center, and study groups.",
      icon: FaUsers,
      features: [
        "Peer Tutoring",
        "Writing Center",
        "Study Groups",
        "Academic Advising",
      ],
    },
    {
      id: 4,
      title: "Career Services",
      description:
        "Professional development and career guidance to help students achieve their goals.",
      icon: FaTrophy,
      features: [
        "Resume Building",
        "Interview Prep",
        "Job Placement",
        "Industry Connections",
      ],
    },
  ];

  const facultySpotlight = [
    {
      id: 1,
      name: "Dr. Sarah Johnson",
      title: "Head of Computer Science",
      expertise: "Artificial Intelligence & Machine Learning",
      image:
        "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80",
      achievements: [
        "15+ Years Experience",
        "Published 50+ Papers",
        "Industry Consultant",
      ],
      quote:
        "Technology is not just about coding; it's about solving real-world problems and making a positive impact.",
    },
    {
      id: 2,
      name: "Prof. Michael Chen",
      title: "Cybersecurity Expert",
      expertise: "Network Security & Digital Forensics",
      image:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80",
      achievements: [
        "20+ Years Experience",
        "Certified Ethical Hacker",
        "Security Consultant",
      ],
      quote:
        "In cybersecurity, we're not just protecting data; we're protecting people's lives and livelihoods.",
    },
    {
      id: 3,
      name: "Dr. Emily Rodriguez",
      title: "Data Science Director",
      expertise: "Big Data Analytics & Business Intelligence",
      image:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=300&q=80",
      achievements: [
        "PhD in Statistics",
        "Industry Experience",
        "Research Publications",
      ],
      quote:
        "Data tells a story. Our job is to help students learn to read and write that story effectively.",
    },
  ];

  const academicCalendar = [
    {
      id: 1,
      event: "Fall Semester Begins",
      date: "September 2, 2025",
      type: "Academic",
    },
    {
      id: 2,
      event: "Midterm Examinations",
      date: "October 15-19, 2025",
      type: "Examination",
    },
    {
      id: 3,
      event: "Research Symposium",
      date: "November 8, 2025",
      type: "Event",
    },
    {
      id: 4,
      event: "Final Examinations",
      date: "December 9-13, 2025",
      type: "Examination",
    },
    {
      id: 5,
      event: "Spring Semester Begins",
      date: "January 13, 2025",
      type: "Academic",
    },
  ];

  const studentSuccessStories = [
    {
      id: 1,
      name: "Alex Kimani",
      program: "Information Technology",
      currentRole: "Senior Software Developer at TechCorp",
      image:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
      quote:
        "The hands-on approach and industry-relevant curriculum at JR Institute gave me the skills and confidence to excel in my career from day one.",
      achievements: [
        "Graduated with Honors",
        "Multiple Industry Certifications",
        "Promoted within 2 years",
      ],
    },
    {
      id: 2,
      name: "Grace Mwangi",
      program: "Cybersecurity",
      currentRole: "Cybersecurity Analyst at BankTanzania",
      image:
        "https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
      quote:
        "The practical labs and real-world projects prepared me for the challenges of protecting critical financial systems.",
      achievements: [
        "Certified Ethical Hacker",
        "Led Security Team",
        "Industry Recognition",
      ],
    },
    {
      id: 3,
      name: "David Ochieng",
      program: "Computer Science",
      currentRole: "Data Scientist at StartupHub",
      image:
        "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80",
      quote:
        "The research opportunities and mentorship I received helped me develop innovative solutions that are now used by thousands of users.",
      achievements: [
        "Published Research",
        "Founded Startup",
        "Industry Awards",
      ],
    },
  ];

  const filteredPrograms = featuredPrograms.filter((program) => {
    const matchesSearch =
      program.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      program.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      selectedCategory === "all" ||
      (selectedCategory === "undergraduate" && program.credits <= "120") ||
      (selectedCategory === "graduate" && program.credits > "120");
    return matchesSearch && matchesCategory;
  });

  return (
    <div>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-custom-pink via-purple-900 to-indigo-900 text-white py-20 lg:py-32">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Academic Excellence
          </h1>
          <p className="text-xl sm:text-2xl mb-8 max-w-3xl mx-auto">
            Discover our comprehensive academic programs designed to prepare you
            for success in the digital age.
          </p>
          <div className="flex flex-wrap justify-center gap-8 text-center">
            <div className="flex items-center gap-2">
              <FaGraduationCap className="text-gold text-2xl" />
              <span className="text-lg font-semibold">15+ Programs</span>
            </div>
            <div className="flex items-center gap-2">
              <FaUsers className="text-gold text-2xl" />
              <span className="text-lg font-semibold">Expert Faculty</span>
            </div>
            <div className="flex items-center gap-2">
              <FaTrophy className="text-gold text-2xl" />
              <span className="text-lg font-semibold">95% Job Placement</span>
            </div>
          </div>
        </div>
      </section>

      {/* Academic Philosophy */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-6">
                {academicPhilosophy.title}
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                {academicPhilosophy.description}
              </p>
              <ul className="space-y-4">
                {academicPhilosophy.principles.map((principle, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="bg-gold text-white p-2 rounded-full">
                      <FaLightbulb className="text-sm" />
                    </div>
                    <span className="text-gray-700 font-medium">
                      {principle}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Students in classroom"
                className="rounded-xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-custom-pink text-white p-6 rounded-xl shadow-lg">
                <div className="text-center">
                  <div className="text-3xl font-bold">95%</div>
                  <div className="text-sm">Graduation Rate</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Programs */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">
              Featured Programs
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Explore our comprehensive range of academic programs designed for
              the digital future.
            </p>

            {/* Search and Filter */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search programs..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-pink focus:border-transparent"
                />
              </div>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-custom-pink focus:border-transparent"
              >
                <option value="all">All Programs</option>
                <option value="undergraduate">Undergraduate</option>
                <option value="graduate">Graduate</option>
              </select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8">
            {filteredPrograms.map((program) => {
              const IconComponent = program.icon;
              return (
                <div
                  key={program.id}
                  className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="bg-custom-pink text-white p-4 rounded-xl">
                      <IconComponent className="text-2xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {program.name}
                      </h3>
                      <p className="text-gray-600">{program.description}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-500">Duration</div>
                      <div className="font-semibold text-gray-900">
                        {program.duration}
                      </div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-500">Credits</div>
                      <div className="font-semibold text-gray-900">
                        {program.credits}
                      </div>
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Career Outcomes:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {program.careerOutcomes.map((outcome, index) => (
                        <span
                          key={index}
                          className="bg-gold text-white px-3 py-1 rounded-full text-sm"
                        >
                          {outcome}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2">
                      Program Highlights:
                    </h4>
                    <ul className="space-y-1">
                      {program.highlights.map((highlight, index) => (
                        <li
                          key={index}
                          className="flex items-center gap-2 text-sm text-gray-600"
                        >
                          <div className="w-1.5 h-1.5 bg-custom-pink rounded-full"></div>
                          {highlight}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button className="w-full bg-custom-pink text-white py-3 px-6 rounded-lg hover:bg-gold transition-colors duration-300 font-semibold flex items-center justify-center gap-2">
                    Learn More
                    <FaArrowRight className="text-sm" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Academic Resources */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">
              Academic Resources
            </h2>
            <p className="text-lg text-gray-600">
              Access comprehensive resources and support services to enhance
              your learning experience.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {academicResources.map((resource) => {
              const IconComponent = resource.icon;
              return (
                <div
                  key={resource.id}
                  className="text-center p-6 bg-gray-50 rounded-xl hover:bg-white hover:shadow-lg transition-all duration-300"
                >
                  <div className="bg-custom-pink text-white p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <IconComponent className="text-2xl" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {resource.title}
                  </h3>
                  <p className="text-gray-600 mb-4 text-sm">
                    {resource.description}
                  </p>
                  <ul className="space-y-1 text-sm text-gray-500">
                    {resource.features.map((feature, index) => (
                      <li key={index}>• {feature}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Faculty Spotlight */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">
              Meet Our Faculty
            </h2>
            <p className="text-lg text-gray-600">
              Learn from industry experts and accomplished researchers who are
              passionate about your success.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {facultySpotlight.map((faculty) => (
              <div
                key={faculty.id}
                className="bg-white rounded-xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow duration-300"
              >
                <img
                  src={faculty.image}
                  alt={faculty.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  {faculty.name}
                </h3>
                <p className="text-gold font-semibold mb-2">{faculty.title}</p>
                <p className="text-gray-600 mb-4">{faculty.expertise}</p>
                <div className="space-y-2 mb-6">
                  {faculty.achievements.map((achievement, index) => (
                    <div key={index} className="text-sm text-gray-500">
                      • {achievement}
                    </div>
                  ))}
                </div>
                <blockquote className="text-gray-700 italic text-sm">
                  "{faculty.quote}"
                </blockquote>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Academic Calendar */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">
              Academic Calendar
            </h2>
            <p className="text-lg text-gray-600">
              Stay updated with important academic dates and events.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {academicCalendar.map((event) => (
                <div
                  key={event.id}
                  className="flex items-center justify-between p-6 bg-gray-50 rounded-xl hover:bg-white hover:shadow-md transition-all duration-300"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`p-3 rounded-full ${event.type === "Academic"
                          ? "bg-custom-pink"
                          : event.type === "Examination"
                            ? "bg-red-500"
                            : "bg-gold"
                        } text-white`}
                    >
                      <FaCalendarAlt className="text-lg" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {event.event}
                      </h3>
                      <p className="text-sm text-gray-500">{event.type}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">
                      {event.date}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Student Success Stories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-custom-pink mb-4">
              Student Success Stories
            </h2>
            <p className="text-lg text-gray-600">
              Hear from our graduates who are making a difference in their
              careers and communities.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {studentSuccessStories.map((story) => (
              <div
                key={story.id}
                className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300"
              >
                <div className="text-center mb-6">
                  <img
                    src={story.image}
                    alt={story.name}
                    className="w-20 h-20 rounded-full mx-auto mb-4 object-cover"
                  />
                  <h3 className="text-lg font-bold text-gray-900">
                    {story.name}
                  </h3>
                  <p className="text-gold font-semibold">{story.program}</p>
                  <p className="text-sm text-gray-600">{story.currentRole}</p>
                </div>
                <blockquote className="text-gray-700 italic mb-6">
                  "{story.quote}"
                </blockquote>
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900">
                    Key Achievements:
                  </h4>
                  {story.achievements.map((achievement, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 text-sm text-gray-600"
                    >
                      <div className="w-1.5 h-1.5 bg-gold rounded-full"></div>
                      {achievement}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <FloatingSocialPanels />
    </div>
  );
};

export default Academics;
